import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import DashboardLayout from '../components/DashboardLayout'
import matchService from '../services/matchService'
import api from '../services/api'
import { T, globalStyles } from '../theme'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import {
  hasScore, resultLetter, scoreDisplay, isWin, isDraw, isDefeat, isCleanSheet,
  goalsFor as goalsForMatch, goalsAgainst as goalsAgainstMatch,
} from '../utils/matchResult'

// Tout le bilan de cette page est du point de vue du CLUB : il passe par
// matchResult, jamais par score_home/score_away bruts, qui sont orientes par
// le terrain. Compares directement, ils inversaient chaque match joue a
// l'exterieur — victoires, defaites, buts pour/contre et clean sheets.
function getRecentForm(matches, n = 5) {
  return [...matches]
    .filter(hasScore)
    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
    .slice(0, n)
    .reverse()
    .map(resultLetter)
}

function getTopScorers(matches) {
  const map = {}
  matches.forEach(m => {
    ;(m.events || []).forEach(ev => {
      if (ev.event_type === 'goal' && ev.player_name) {
        if (!map[ev.player_name]) map[ev.player_name] = { name: ev.player_name, goals: 0, assists: 0 }
        map[ev.player_name].goals++
      }
      if (ev.event_type === 'assist' && ev.player_name) {
        if (!map[ev.player_name]) map[ev.player_name] = { name: ev.player_name, goals: 0, assists: 0 }
        map[ev.player_name].assists++
      }
    })
  })
  return Object.values(map).sort((a, b) => b.goals - a.goals || b.assists - a.assists)
}

const RESULT_COLOR = { V: '#22c55e', N: '#f59e0b', D: '#ef4444' }
// LES TROIS TYPES, SEPAREMENT.
//
// `OFFICIAL_TYPES` regroupait CHAMPIONNAT et COUPE sous « Officiels » · le
// coach ne pouvait pas isoler son championnat, alors que c'est la seule
// competition ou son classement se joue.
//
// L'ordre de ce tableau est celui des onglets · il ne se derive pas des
// donnees, sinon les options bougeraient de place d'un club a l'autre.
const TYPES_MATCH = [
  { id: 'championnat', label: 'Championnat' },
  { id: 'coupe',       label: 'Coupe'       },
  { id: 'amical',      label: 'Amical'      },
]

// L'API rend le type en MINUSCULES (`_serialize_match` · `match.type.value`),
// et c'est le contrat (MATCHTYPE-001). On normalise quand meme · comparer une
// casse supposee a une casse rendue a deja produit un defaut muet sur l'ecran
// du match, ou le `<select>` n'affichait jamais la vraie valeur.
const typeDeMatch = (m) => (m?.type || '').toLowerCase()

const TYPE_LABEL = {
  'CHAMPIONNAT': 'Champ.', 'championnat': 'Champ.',
  'COUPE': 'Coupe', 'coupe': 'Coupe',
  'AMICAL': 'Amical', 'amical': 'Amical',
}

function PitchWatermark() {
  return (
    <svg
      viewBox="0 0 400 140"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.07, pointerEvents: 'none' }}
    >
      <rect x="12" y="8" width="376" height="124" fill="none" stroke="#f5f2eb" strokeWidth="1.5" />
      <line x1="200" y1="8" x2="200" y2="132" stroke="#f5f2eb" strokeWidth="1.5" />
      <circle cx="200" cy="70" r="26" fill="none" stroke="#f5f2eb" strokeWidth="1.5" />
      <circle cx="200" cy="70" r="2" fill="#f5f2eb" />
      <rect x="12" y="38" width="58" height="64" fill="none" stroke="#f5f2eb" strokeWidth="1.5" />
      <rect x="330" y="38" width="58" height="64" fill="none" stroke="#f5f2eb" strokeWidth="1.5" />
    </svg>
  )
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: T.dark, border: `1px solid ${T.rule}`, padding: '10px 14px' }}>
      <p style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginBottom: 6 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ fontFamily: T.mono, fontSize: 10, color: p.color, margin: '2px 0' }}>
          {p.name} : {p.value}
        </p>
      ))}
    </div>
  )
}

function ProgressionChart({ chartData, isMobile }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '16px 20px', borderRadius: 3 }}>
      <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 2, height: 12, background: T.gold }} />Progression de la saison
      </p>
      <div style={{ display: 'flex', gap: 18, marginBottom: 14 }}>
        <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 16, height: 2, background: T.gold, display: 'inline-block', borderRadius: 1 }} />Buts marqués
        </span>
        <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 16, height: 2, background: '#ef4444', display: 'inline-block', borderRadius: 1 }} />Buts encaissés
        </span>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={T.rule} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontFamily: T.mono, fontSize: 8, fill: T.muted }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontFamily: T.mono, fontSize: 8, fill: T.muted }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip content={<ChartTooltip />} />
          <Line
            type="monotone"
            dataKey="pour"
            name="Buts marqués"
            stroke={T.gold}
            strokeWidth={2}
            dot={{ fill: T.gold, r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
          <Line
            type="monotone"
            dataKey="contre"
            name="Buts encaissés"
            stroke="#ef4444"
            strokeWidth={2}
            dot={{ fill: '#ef4444', r: 3, strokeWidth: 0 }}
            activeDot={{ r: 5, strokeWidth: 0 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function ComparerLocked({ segment, isMobile, onCopy, copied }) {
  const niveauLabel = [segment?.category, segment?.level].filter(Boolean).join(' ')
  return (
    <div style={{
      background: T.surface,
      border: `1px solid ${T.rule}`,
      borderTop: `2px solid ${T.goldBdr}`,
      padding: isMobile ? '40px 24px' : '64px 48px',
      textAlign: 'center',
      borderRadius: 3,
    }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 56, height: 56,
        background: T.goldBg, border: `1px solid ${T.goldBdr}`,
        borderRadius: '50%', marginBottom: 24,
      }}>
        <Lock size={24} color={T.gold} />
      </div>
      <h2 style={{
        fontFamily: T.display, fontSize: isMobile ? 24 : 32,
        textTransform: 'uppercase', lineHeight: 1,
        color: T.ink, marginBottom: 16,
      }}>
        Comparatif verrouillé
      </h2>
      <p style={{
        fontFamily: T.mono, fontSize: 12, color: T.ink,
        lineHeight: 1.7, marginBottom: 8,
        maxWidth: 440, marginLeft: 'auto', marginRight: 'auto',
      }}>
        Le comparatif se débloque quand <strong>5 clubs de ton niveau</strong> utilisent Insightball.
      </p>
      {niveauLabel && (
        <p style={{
          fontFamily: T.mono, fontSize: 11, color: T.muted,
          lineHeight: 1.7, marginBottom: 32,
          maxWidth: 440, marginLeft: 'auto', marginRight: 'auto',
        }}>
          Invite des coachs de <strong style={{ color: T.ink }}>{niveauLabel}</strong> à rejoindre Insightball pour débloquer cette fonctionnalité.
        </p>
      )}
      {!niveauLabel && <div style={{ marginBottom: 32 }} />}
      <button
        onClick={onCopy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '11px 24px',
          background: copied ? 'transparent' : T.gold,
          color: copied ? '#22c55e' : T.dark,
          fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
          border: copied ? '1px solid #22c55e40' : 'none',
          cursor: 'pointer', transition: 'all .2s',
        }}
      >
        {copied ? 'Lien copié !' : "Copier le lien d'invitation"}
      </button>
    </div>
  )
}

function ComparerNoSegment({ isMobile }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.rule}`,
      padding: isMobile ? '40px 24px' : '64px 48px',
      textAlign: 'center', borderRadius: 3,
    }}>
      <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 12 }}>
        Comparatif non disponible
      </p>
      <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 22 : 28, textTransform: 'uppercase', lineHeight: 1, color: T.ink, marginBottom: 16 }}>
        Déclare ton niveau<br />pour activer le comparatif.
      </h2>
      <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7, marginBottom: 28, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
        Tu dois indiquer la catégorie et le niveau de ton équipe pour accéder au comparatif national.
      </p>
      <Link to="/dashboard/benchmark" style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '11px 22px', background: T.gold, color: T.dark,
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
        textDecoration: 'none',
      }}>
        Déclarer mon niveau →
      </Link>
    </div>
  )
}

function ComparerRow({ label, mine, segment, top25, format = 'pct', higherIsBetter = true }) {
  const fmt = v => {
    if (v == null) return '—'
    if (format === 'pct') return `${v}%`
    if (format === 'avg') return typeof v === 'number' ? v.toFixed(2).replace('.', ',') : v
    return v
  }
  let arrow = null
  if (mine != null && segment != null) {
    const diff = mine - segment
    const threshold = format === 'pct' ? 2 : 0.1
    if (Math.abs(diff) >= threshold) {
      const isGood = higherIsBetter ? diff > 0 : diff < 0
      arrow = (
        <span style={{ fontFamily: T.mono, fontSize: 11, color: isGood ? '#22c55e' : '#ef4444' }}>
          {isGood ? '↑' : '↓'}
        </span>
      )
    }
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      alignItems: 'center', gap: 8,
      padding: '12px 18px',
      borderBottom: `1px solid ${T.rule}`,
    }}>
      <span style={{ fontFamily: T.mono, fontSize: 10, color: T.ink }}>{label}</span>
      <span style={{ fontFamily: T.display, fontSize: 18, color: T.gold, textAlign: 'right' }}>{fmt(mine)}</span>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, textAlign: 'right' }}>{fmt(segment)}</span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
        <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted }}>{fmt(top25)}</span>
        {arrow}
      </div>
    </div>
  )
}

function ComparerUnlocked({ data, isMobile }) {
  const { segment, eligible_count, my_stats, my_stats_unlocked, segment_stats, top25_stats } = data
  const niveauLabel = [segment?.category, segment?.level].filter(Boolean).join(' ')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        background: T.dark, borderTop: `2px solid ${T.gold}`,
        padding: isMobile ? '20px 20px' : '24px 28px', borderRadius: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          {niveauLabel && (
            <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.gold, marginBottom: 6 }}>
              {niveauLabel}
            </p>
          )}
          <p style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 36, textTransform: 'uppercase', color: '#f5f2eb', lineHeight: 1 }}>
            {eligible_count} clubs comparés
          </p>
        </div>
        {my_stats && (
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <p style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Tes matchs</p>
            <p style={{ fontFamily: T.display, fontSize: 24, color: T.gold, lineHeight: 1 }}>{my_stats.matches_count}</p>
          </div>
        )}
      </div>

      <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
          padding: '10px 18px', borderBottom: `1px solid ${T.rule}`,
          background: T.bg,
        }}>
          <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted }}>Métrique</span>
          <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.gold, textAlign: 'right' }}>Toi</span>
          <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, textAlign: 'right' }}>Niveau</span>
          <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: T.muted, textAlign: 'right' }}>Top 25%</span>
        </div>

        {!my_stats_unlocked && (
          <div style={{ padding: '16px 18px', borderBottom: `1px solid ${T.rule}`, background: T.goldBg }}>
            <p style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, lineHeight: 1.6 }}>
              Il te faut au moins <strong>5 matchs de championnat</strong> sur cette saison pour afficher tes stats comparées.
              {my_stats?.matches_count > 0 && ` Tu en as ${my_stats.matches_count} pour l'instant.`}
            </p>
          </div>
        )}

        <ComparerRow label="Victoires"              mine={my_stats_unlocked ? my_stats?.wins_pct : null}           segment={segment_stats?.wins_pct}           top25={top25_stats?.wins_pct}           format="pct" higherIsBetter />
        <ComparerRow label="Nuls"                   mine={my_stats_unlocked ? my_stats?.draws_pct : null}          segment={segment_stats?.draws_pct}          top25={top25_stats?.draws_pct}          format="pct" higherIsBetter={false} />
        <ComparerRow label="Défaites"               mine={my_stats_unlocked ? my_stats?.losses_pct : null}         segment={segment_stats?.losses_pct}         top25={top25_stats?.losses_pct}         format="pct" higherIsBetter={false} />
        <ComparerRow label="Buts marqués / match"   mine={my_stats_unlocked ? my_stats?.goals_scored_avg : null}   segment={segment_stats?.goals_scored_avg}   top25={top25_stats?.goals_scored_avg}   format="avg" higherIsBetter />
        <ComparerRow label="Buts encaissés / match" mine={my_stats_unlocked ? my_stats?.goals_conceded_avg : null} segment={segment_stats?.goals_conceded_avg} top25={top25_stats?.goals_conceded_avg} format="avg" higherIsBetter={false} />
        <ComparerRow label="Clean sheets"           mine={my_stats_unlocked ? my_stats?.clean_sheets_pct : null}   segment={segment_stats?.clean_sheets_pct}   top25={top25_stats?.clean_sheets_pct}   format="pct" higherIsBetter />
      </div>
    </div>
  )
}

export default function Statistics() {
  const { selectedSeason, isNextSeason, isPastSeason } = useSeason()
  const [matches,         setMatches]         = useState([])
  const [loading,         setLoading]         = useState(true)
  // SELECTION MULTIPLE · un tableau de types actifs. Le tableau VIDE vaut
  // « Tout » · c'est l'absence de restriction, pas une quatrieme valeur a
  // laquelle il faudrait penser partout. Decocher le dernier type revient donc
  // a « Tout », plutot qu'a un ecran vide que personne n'a demande.
  const [typesActifs, setTypesActifs] = useState([])
  const [isMobile,        setIsMobile]        = useState(() => window.innerWidth < 768)
  const [activeTab,       setActiveTab]       = useState('mon-equipe')
  const [bData,           setBData]           = useState(null)
  const [bLoading,        setBLoading]        = useState(false)
  const [bError,          setBError]          = useState(false)
  const [bLoaded,         setBLoaded]         = useState(false)
  const [copied,          setCopied]          = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    setLoading(true)
    ;(async () => {
      // LA SAISON EST TRANSMISE. Sans elle, `GET /matches/` renvoyait TOUTES
      // les saisons du club, alors que le titre de la page annonce celle qui
      // est selectionnee. L'effet dependait deja de `selectedSeason` et
      // relancait donc une requete identique · changer de saison ne changeait
      // rien a l'ecran.
      //
      // Ce n'etait pas theorique · mesure le 25/08/2026, trois clubs ont des
      // matchs dans deux saisons, dont JS Cugnaux, le plus gros club reel du
      // produit · 7 matchs de championnat en 2025-2026 et 19 en 2026-2027. Un
      // de ses coachs lisait 26 matchs sous un titre disant « 2026-2027 ».
      //
      // L'onglet « Comparer » envoyait deja `season` a `/benchmark/` · les deux
      // onglets de cette page portaient sur deux perimetres differents.
      try { const m = await matchService.getMatches({ season: selectedSeason }); setMatches(m) }
      catch { /* handled by empty state */ }
      finally { setLoading(false) }
    })()
  }, [selectedSeason])

  const loadBenchmark = async () => {
    setBLoading(true)
    setBError(false)
    try {
      const { data } = await api.get('/benchmark/', { params: { season: selectedSeason } })
      setBData(data)
    } catch {
      setBError(true)
    } finally {
      setBLoading(false)
      setBLoaded(true)
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    if (tab === 'comparer' && !bLoaded) loadBenchmark()
  }

  const handleCopyInvite = () => {
    navigator.clipboard.writeText('https://insightball.com?ref=compare')
    setCopied(true)
    setTimeout(() => setCopied(false), 3000)
  }

  const allCompleted = matches.filter(m =>
    m.status === 'completed' || m.status === 'COMPLETED' ||
    m.status === 'analyzed'  || m.status === 'ANALYZED'
  )

  const filtered = typesActifs.length === 0
    ? allCompleted
    : allCompleted.filter(m => typesActifs.includes(typeDeMatch(m)))

  const basculerType = (id) => setTypesActifs(prev =>
    prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
  )

  // LES COMPTEURS SE CALCULENT SUR `allCompleted`, JAMAIS SUR `filtered` ·
  // sinon un type deselectionne tomberait a zero et deviendrait ingrisable,
  // donc impossible a re-selectionner.
  //
  // LES QUATRE OPTIONS SONT TOUJOURS AFFICHEES, celles a zero grisees et non
  // cliquables. Les masquer les faisait apparaitre et disparaitre au fil des
  // donnees · illisible des lors qu'on peut en cocher plusieurs. La coupe
  // vaut zero pour presque tous les clubs (1 match dans toute la production au
  // 25/08/2026) · c'est le cas normal, pas un cas limite.
  const TYPES_AVEC_COMPTE = TYPES_MATCH.map(t => ({
    ...t,
    count: allCompleted.filter(m => typeDeMatch(m) === t.id).length,
  }))

  const withScore   = filtered.filter(hasScore)
  const wins        = withScore.filter(isWin).length
  const draws       = withScore.filter(isDraw).length
  const losses      = withScore.filter(isDefeat).length
  const goalsFor    = withScore.reduce((s, m) => s + (goalsForMatch(m) || 0), 0)
  const goalsAgt    = withScore.reduce((s, m) => s + (goalsAgainstMatch(m) || 0), 0)
  const cleanSheets = withScore.filter(isCleanSheet).length
  const avgFor      = withScore.length ? (goalsFor / withScore.length).toFixed(1) : '0.0'
  const recentForm  = getRecentForm(filtered)
  // SUIT LE FILTRE, comme tous les autres agregats · il lisait `allCompleted`
  // et affichait donc des buteurs de championnat a un coach qui avait isole
  // ses amicaux. Aucun autre indicateur de l'ecran n'avait ce defaut.
  const topScorers  = getTopScorers(filtered)
  const hasEvents   = topScorers.length > 0

  const sortedMatches = [...filtered].sort(
    (a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at)
  )

  const chartData = [...withScore]
    .sort((a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at))
    .map(m => ({
      label: m.date ? new Date(m.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '',
      pour: goalsForMatch(m),
      contre: goalsAgainstMatch(m),
    }))

  const Spinner = () => (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <div style={{ width: 26, height: 26, border: `2px solid ${T.goldBdr}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto' }} />
    </div>
  )

  return (
    <DashboardLayout>
      <style>{globalStyles}</style>

      {/* HEADER */}
      <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: `0.5px solid ${T.rule}` }}>
        <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
          Statistiques · Saison {formatSeasonShort(selectedSeason)}
        </p>
        <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 36, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-.01em', color: T.ink, margin: 0, marginBottom: 16 }}>
          Analyse de la saison
        </h1>

        {/* Onglets principaux */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `0.5px solid ${T.rule}` }}>
          {[
            { id: 'mon-equipe', label: 'Mon équipe' },
            { id: 'comparer',   label: 'Comparer'   },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              style={{
                padding: '10px 18px',
                fontFamily: T.mono, fontSize: 12, fontWeight: activeTab === tab.id ? 600 : 400,
                background: 'transparent', border: 'none', cursor: 'pointer', transition: 'all .12s',
                borderBottom: activeTab === tab.id && !loading ? `2px solid ${T.ink}` : '2px solid transparent',
                color: activeTab === tab.id && !loading ? T.ink : T.muted,
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Sous-filtre par type — uniquement onglet Mon équipe.

            PAS SUR « COMPARER », ET CE N'EST PAS UN OUBLI · le comparatif est
            calcule cote serveur sur le championnat uniquement
            (`benchmark.py`, `Match.type == MatchType.CHAMPIONNAT`, pour le
            coach ET pour son segment). Un filtre client ne l'atteindrait pas,
            et le laisser visible la-bas laisserait croire l'inverse. */}
        {activeTab === 'mon-equipe' && !loading && allCompleted.length > 0 && (
          <div style={{ display: 'flex', gap: 0, borderBottom: `0.5px solid ${T.rule}`, overflowX: 'auto', marginTop: 8 }}>
            {(() => {
              const toutActif = typesActifs.length === 0
              const styleOnglet = (actif, desactive) => ({
                padding: '7px 14px', fontFamily: T.mono, fontSize: 10, fontWeight: actif ? 600 : 400,
                background: 'transparent', border: 'none',
                borderBottom: actif ? `2px solid ${T.ink}` : '2px solid transparent',
                color: desactive ? T.muted2 : actif ? T.ink : T.muted,
                cursor: desactive ? 'not-allowed' : 'pointer',
                opacity: desactive ? 0.45 : 1,
                transition: 'all .12s', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 5, marginBottom: -1,
              })
              return (
                <>
                  {/* « Tout » n'est pas un type · c'est la remise a zero de la
                      selection. D'ou `setTypesActifs([])` et non un identifiant
                      qu'il faudrait ensuite exclure des filtres. */}
                  <button
                    onClick={() => setTypesActifs([])}
                    aria-pressed={toutActif}
                    style={styleOnglet(toutActif, false)}
                  >
                    Tout
                    <span style={{ fontFamily: T.mono, fontSize: 9, color: toutActif ? T.muted : T.muted2 }}>{allCompleted.length}</span>
                  </button>
                  {TYPES_AVEC_COMPTE.map(t => {
                    const actif = typesActifs.includes(t.id)
                    const vide  = t.count === 0
                    return (
                      <button
                        key={t.id}
                        onClick={() => basculerType(t.id)}
                        disabled={vide}
                        aria-pressed={actif}
                        title={vide ? 'Aucun match de ce type sur la saison' : undefined}
                        style={styleOnglet(actif, vide)}
                      >
                        {t.label}
                        <span style={{ fontFamily: T.mono, fontSize: 9, color: actif ? T.muted : T.muted2 }}>{t.count}</span>
                      </button>
                    )
                  })}
                </>
              )
            })()}
          </div>
        )}
      </div>

      {/* ── Onglet Mon équipe ── */}
      {activeTab === 'mon-equipe' ? (

        loading ? <Spinner /> : filtered.length === 0 ? (

          <div style={{ background: T.dark, padding: isMobile ? '32px 24px' : '48px 40px', borderTop: `2px solid ${T.gold}`, position: 'relative', overflow: 'hidden', borderRadius: 3 }}>
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .04, backgroundImage: 'linear-gradient(rgba(79,142,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(79,142,247,1) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
            <div style={{ position: 'relative' }}>
              {isNextSeason ? (
                <>
                  <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.7)', marginBottom: 12 }}>
                    Saison {formatSeasonShort(selectedSeason)} · à venir
                  </p>
                  <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 40, textTransform: 'uppercase', lineHeight: .9, color: '#f5f2eb', marginBottom: 16 }}>
                    La saison<br /><span style={{ color: T.gold }}>n'a pas commencé.</span>
                  </h2>
                  <p style={{ fontFamily: T.mono, fontSize: 11, color: 'rgba(245,242,235,0.45)', lineHeight: 1.7, marginBottom: 24, maxWidth: 420 }}>
                    Tes statistiques apparaîtront ici dès tes premiers matchs de la saison {formatSeasonShort(selectedSeason)}.
                    En attendant, tu peux préparer ton effectif.
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <Link to="/dashboard/players" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '11px 20px', background: T.gold, color: T.dark,
                      fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                      textDecoration: 'none',
                    }}>
                      Préparer l'effectif →
                    </Link>
                    <Link to="/dashboard/matches/new" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '11px 20px', background: 'transparent', color: '#f5f2eb',
                      fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase',
                      border: '1px solid rgba(245,242,235,0.2)', textDecoration: 'none',
                    }}>
                      Saisir un match
                    </Link>
                  </div>
                </>
              ) : isPastSeason ? (
                <>
                  <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.7)', marginBottom: 12 }}>
                    Saison {formatSeasonShort(selectedSeason)} · archive
                  </p>
                  <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 40, textTransform: 'uppercase', lineHeight: .9, color: '#f5f2eb', marginBottom: 16 }}>
                    Aucun match<br /><span style={{ color: T.gold }}>sur cette saison.</span>
                  </h2>
                  <p style={{ fontFamily: T.mono, fontSize: 11, color: 'rgba(245,242,235,0.45)', lineHeight: 1.7, marginBottom: 24, maxWidth: 380 }}>
                    Tu n'avais pas encore Insightball lors de la saison {formatSeasonShort(selectedSeason)}, ou aucun match n'a été enregistré.
                  </p>
                  <Link to="/dashboard/matches" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '11px 20px', background: 'transparent', color: T.gold,
                    fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase',
                    border: `1px solid ${T.goldBdr}`, textDecoration: 'none',
                  }}>
                    Revenir à la saison en cours →
                  </Link>
                </>
              ) : (
                <>
                  <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.7)', marginBottom: 12 }}>Aucun match</p>
                  <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 40, textTransform: 'uppercase', lineHeight: .9, color: '#f5f2eb', marginBottom: 16 }}>
                    Analyse ton premier<br /><span style={{ color: T.gold }}>match.</span>
                  </h2>
                  <p style={{ fontFamily: T.mono, fontSize: 11, color: 'rgba(245,242,235,0.45)', lineHeight: 1.7, marginBottom: 24, maxWidth: 380 }}>
                    Analyse ton premier match pour voir ta progression ici.
                  </p>
                  <Link to="/dashboard/matches/new" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '11px 20px', background: T.gold, color: T.dark,
                    fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                    textDecoration: 'none',
                  }}>
                    Nouveau match
                  </Link>
                </>
              )}
            </div>
          </div>

        ) : (

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Progression */}
            {withScore.length === 0 ? (
              <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '20px 24px', borderRadius: 3 }}>
                <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.6, marginBottom: 10 }}>
                  Analyse ton premier match pour voir ta progression.
                </p>
                <Link to="/dashboard/matches" style={{ fontFamily: T.mono, fontSize: 9, color: T.gold, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none' }}>
                  Voir les matchs →
                </Link>
              </div>
            ) : withScore.length === 1 ? (
              <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '20px 24px', borderRadius: 3 }}>
                <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
                  Analyse encore <strong style={{ color: T.ink }}>4 matchs</strong> pour voir les courbes se dessiner.
                </p>
              </div>
            ) : (
              <ProgressionChart chartData={chartData} isMobile={isMobile} />
            )}

            {/* KPI bloc */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8 }}>
              {[
                { label: 'Matchs',    value: withScore.length, accentColor: T.accentStat1 },
                { label: 'Victoires', value: wins,             accentColor: T.green        },
                { label: 'Nuls',      value: draws,            accentColor: T.gold         },
                { label: 'Défaites',  value: losses,           accentColor: T.red          },
              ].map(k => (
                <div key={k.label} style={{ background: T.surface, border: `0.5px solid ${T.rule}`, borderTop: `2.5px solid ${k.accentColor}`, padding: isMobile ? '14px 14px' : '18px 20px' }}>
                  <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2, marginBottom: 8 }}>{k.label}</p>
                  <p style={{ fontFamily: T.display, fontSize: isMobile ? 32 : 40, fontWeight: 800, lineHeight: 1, color: T.ink, margin: 0 }}>{k.value}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 8 }}>
              {[
                { label: 'Buts marqués',   value: goalsFor    },
                { label: 'Buts encaissés', value: goalsAgt    },
                { label: 'Moy. marqués',   value: avgFor      },
                { label: 'Clean sheets',   value: cleanSheets },
              ].map(k => (
                <div key={k.label} style={{ background: T.surface, border: `0.5px solid ${T.rule}`, padding: isMobile ? '12px 14px' : '14px 20px' }}>
                  <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2, marginBottom: 6 }}>{k.label}</p>
                  <p style={{ fontFamily: T.display, fontSize: isMobile ? 22 : 28, fontWeight: 700, lineHeight: 1, color: T.ink, margin: 0 }}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* Grille principale */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : hasEvents ? '1fr 260px' : '1fr', gap: 16 }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {recentForm.length > 0 && (
                  <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '16px 20px', borderRadius: 3 }}>
                    <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 2, height: 12, background: T.gold }} />Forme récente
                    </p>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      {recentForm.map((r, i) => (
                        <div key={i} style={{
                          width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: RESULT_COLOR[r] + '15', border: `1px solid ${RESULT_COLOR[r]}50`,
                          fontFamily: T.display, fontSize: 15, color: RESULT_COLOR[r],
                        }}>{r}</div>
                      ))}
                      {Array.from({ length: Math.max(0, 5 - recentForm.length) }).map((_, i) => (
                        <div key={'e' + i} style={{ width: 34, height: 34, border: `1px solid ${T.rule}`, background: T.bg }} />
                      ))}
                      <span style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, marginLeft: 8, letterSpacing: '.06em' }}>5 derniers matchs</span>
                    </div>
                  </div>
                )}

                <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 12, height: 1, background: T.gold }} />Tous les matchs ({sortedMatches.length})
                  </div>
                  {sortedMatches.map((m, i) => {
                    const scored = hasScore(m)
                    const rl   = resultLetter(m)
                    const rc   = rl === 'V' ? '#22c55e' : rl === 'N' ? '#f59e0b' : '#ef4444'
                    const dateStr = m.date
                      ? new Date(m.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
                      : ''
                    return (
                      <Link
                        key={m.id}
                        to={`/dashboard/matches/${m.id}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '11px 20px',
                          borderBottom: i < sortedMatches.length - 1 ? `1px solid ${T.rule}` : 'none',
                          borderLeft: scored ? `2px solid ${rc}` : `2px solid ${T.rule}`,
                          textDecoration: 'none',
                          transition: 'background .1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = T.bg}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        {scored && (
                          <span style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 700, padding: '2px 5px', background: rc + '18', color: rc, flexShrink: 0, minWidth: 14, textAlign: 'center' }}>{rl}</span>
                        )}
                        {dateStr && (
                          <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, flexShrink: 0, minWidth: 32 }}>{dateStr}</span>
                        )}
                        <span style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          vs {m.opponent}
                        </span>
                        {m.type && (
                          <span style={{ fontFamily: T.mono, fontSize: 8, color: T.muted, letterSpacing: '.06em', flexShrink: 0 }}>
                            {TYPE_LABEL[m.type] || m.type}
                          </span>
                        )}
                        {scored && (
                          <span style={{ fontFamily: T.display, fontSize: 15, color: T.ink, flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
                            {scoreDisplay(m, '–')}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>

              {hasEvents && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid ${T.gold}`, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 12, height: 1, background: T.gold }} />Buteurs
                    </div>
                    {topScorers.filter(p => p.goals > 0).slice(0, 8).map((p, i, arr) => (
                      <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < arr.length - 1 ? `1px solid ${T.rule}` : 'none' }}>
                        <span style={{ fontFamily: T.display, fontSize: 11, color: T.muted, minWidth: 14 }}>{i + 1}</span>
                        <span style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, flex: 1 }}>{p.name}</span>
                        <span style={{ fontFamily: T.display, fontSize: 20, color: T.gold }}>{p.goals}</span>
                        <span style={{ fontFamily: T.mono, fontSize: 7, color: T.muted, letterSpacing: '.08em' }}>BUTS</span>
                      </div>
                    ))}
                  </div>

                  {topScorers.some(p => p.assists > 0) && (
                    <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid rgba(79,142,247,0.3)`, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 12, height: 1, background: T.muted }} />Passeurs décisifs
                      </div>
                      {topScorers.filter(p => p.assists > 0).sort((a, b) => b.assists - a.assists).slice(0, 5).map((p, i, arr) => (
                        <div key={p.name + 'a'} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < arr.length - 1 ? `1px solid ${T.rule}` : 'none' }}>
                          <span style={{ fontFamily: T.display, fontSize: 11, color: T.muted, minWidth: 14 }}>{i + 1}</span>
                          <span style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, flex: 1 }}>{p.name}</span>
                          <span style={{ fontFamily: T.display, fontSize: 20, color: 'rgba(79,142,247,0.65)' }}>{p.assists}</span>
                          <span style={{ fontFamily: T.mono, fontSize: 7, color: T.muted, letterSpacing: '.08em' }}>PD</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )

      ) : (

        /* ── Onglet Comparer ── */
        bLoading ? <Spinner /> : bError ? (
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '40px 24px', textAlign: 'center', borderRadius: 3 }}>
            <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, marginBottom: 16 }}>
              Impossible de charger le comparatif. Réessaie dans quelques instants.
            </p>
            <button onClick={loadBenchmark} style={{
              padding: '10px 20px', background: T.gold, color: T.dark,
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
              border: 'none', cursor: 'pointer',
            }}>
              Réessayer
            </button>
          </div>
        ) : !bData ? null
          : bData.reason === 'no_segment_declared' ? <ComparerNoSegment isMobile={isMobile} />
          : bData.unlocked === false               ? <ComparerLocked segment={bData.segment} isMobile={isMobile} onCopy={handleCopyInvite} copied={copied} />
          : bData.unlocked === true                ? <ComparerUnlocked data={bData} isMobile={isMobile} />
          : null
      )}
    </DashboardLayout>
  )
}
